import React, { useEffect, useState } from "react";
import { Modal, ModalDialog } from "@mui/joy";
import { Close } from "@mui/icons-material";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { postFetch } from "../../../../fetch/fetch";
import styles from "./faqadmin.module.css";
import { toastOptions } from "./faqutil";

/**
 * 카테고리 관리 팝업 (목업 AdminFaq 420px) — 드로어의 (+) 버튼에서 열림
 *  - 목록: ⋮⋮ 드래그로 순서 변경(POST /categories/reorder) · 이름 변경(인라인 입력) · 삭제(항목 0개일 때만)
 *  - 하단: 새 카테고리 이름 + [추가] → 추가 즉시 상위 셀렉트에 반영·선택 (onCreated)
 *  - 이름을 바꾸면 서버가 소속 faq_item.category 를 일괄 UPDATE → 그 항목들이 '미배포' 가 된다
 */
const FaqCategoryModal : React.FC<any> = (props : any) => {

    const [list, setList] = useState<any[]>([]);
    const [newName, setNewName] = useState("");
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editingName, setEditingName] = useState("");
    const [dragIndex, setDragIndex] = useState<number | null>(null);
    const [overIndex, setOverIndex] = useState<number | null>(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (props.open) {
            setList(props.categories || []);
            setEditingId(null);
            setNewName("");
        }
    }, [props.open, props.categories]);

    const add = async () => {
        const name = newName.trim();
        if (!name) {
            return;
        }
        setBusy(true);
        try {
            const result : any = await postFetch("/api/faq/categories", { name });
            if (result && result.message === "success") {
                toast.success(`'${name}' 추가됨`, toastOptions);
                setNewName("");
                await props.onChanged();
                if (props.onCreated) {
                    props.onCreated(name);
                }
            } else if (result && result.message === "duplicate") {
                toast.warning("같은 이름의 카테고리가 이미 있습니다", toastOptions);
            } else {
                toast.error("추가에 실패했습니다", toastOptions);
            }
        } catch (e) {
            console.log(e);
            toast.error("추가에 실패했습니다", toastOptions);
        } finally {
            setBusy(false);
        }
    };

    const rename = async (c : any) => {
        const name = editingName.trim();
        if (!name || name === c.name) {
            setEditingId(null);
            return;
        }
        setBusy(true);
        try {
            const result : any = await postFetch(`/api/faq/categories/${c.id}/update`, { name });
            if (result && result.message === "success") {
                toast.success(`'${c.name}' → '${name}' (소속 ${c.count}건 미배포 상태)`, toastOptions);
                setEditingId(null);
                await props.onChanged();
            } else if (result && result.message === "duplicate") {
                toast.warning("같은 이름의 카테고리가 이미 있습니다", toastOptions);
            } else {
                toast.error("이름 변경에 실패했습니다", toastOptions);
            }
        } catch (e) {
            console.log(e);
            toast.error("이름 변경에 실패했습니다", toastOptions);
        } finally {
            setBusy(false);
        }
    };

    const remove = async (c : any) => {
        setBusy(true);
        try {
            const result : any = await postFetch(`/api/faq/categories/${c.id}/delete`, {});
            if (result && result.message === "success") {
                toast.success(`'${c.name}' 삭제됨`, toastOptions);
                await props.onChanged();
            } else if (result && result.message === "hasItems") {
                toast.warning(`소속 항목 ${result.count}건이 있어 삭제할 수 없습니다`, toastOptions);
            } else {
                toast.error("삭제에 실패했습니다", toastOptions);
            }
        } catch (e) {
            console.log(e);
            toast.error("삭제에 실패했습니다", toastOptions);
        } finally {
            setBusy(false);
        }
    };

    const onDrop = async (toIndex : number) => {
        if (dragIndex === null || dragIndex === toIndex) {
            setDragIndex(null);
            setOverIndex(null);
            return;
        }
        const next = [...list];
        const [moved] = next.splice(dragIndex, 1);
        next.splice(toIndex, 0, moved);
        setList(next);
        setDragIndex(null);
        setOverIndex(null);
        try {
            const result : any = await postFetch("/api/faq/categories/reorder", { ids : next.map((c) => c.id) });
            if (result && result.message === "success") {
                await props.onChanged();
            } else {
                toast.error("순서 저장에 실패했습니다", toastOptions);
            }
        } catch (e) {
            console.log(e);
            toast.error("순서 저장에 실패했습니다", toastOptions);
        }
    };

    return (
        <Modal open={props.open} onClose={props.onClose}>
            <ModalDialog sx={{ padding : 0, borderRadius : "14px", width : "440px", maxWidth : "95vw" }}>
                <div className={styles.modal} style={{ boxShadow : "none" }}>
                    <div className={styles.modalHeader}>
                        카테고리 관리
                        <button className={styles.closeBtn} aria-label="닫기" onClick={props.onClose}><Close sx={{ fontSize : 20 }} /></button>
                    </div>
                    <div className={styles.modalBody} style={{ padding : "14px 22px", maxHeight : "380px" }}>
                        {list.map((c : any, idx : number) => (
                            <div
                                key={c.id}
                                className={`${styles.catRow} ${dragIndex === idx ? styles.dragging : ""} ${overIndex === idx ? styles.dragOver : ""}`}
                                draggable={editingId === null}
                                onDragStart={() => setDragIndex(idx)}
                                onDragOver={(e) => { e.preventDefault(); if (overIndex !== idx) setOverIndex(idx); }}
                                onDragLeave={() => { if (overIndex === idx) setOverIndex(null); }}
                                onDrop={(e) => { e.preventDefault(); onDrop(idx); }}
                                onDragEnd={() => { setDragIndex(null); setOverIndex(null); }}
                            >
                                <span className={styles.handle} title="드래그해서 순서 변경">⋮⋮</span>
                                {
                                    editingId === c.id ?
                                        <>
                                            <input className={styles.input} style={{ height : "32px", flex : 1 }} value={editingName} autoFocus onChange={(e) => setEditingName(e.target.value)}
                                                onKeyDown={(e) => { if (e.key === "Enter") rename(c); if (e.key === "Escape") setEditingId(null); }} />
                                            <button className={`${styles.btn} ${styles.btnSm} ${styles.btnPrimary}`} disabled={busy} onClick={() => rename(c)}>저장</button>
                                            <button className={`${styles.btn} ${styles.btnSm}`} onClick={() => setEditingId(null)}>취소</button>
                                        </>
                                        :
                                        <>
                                            <span className={styles.catName}>{c.name}</span>
                                            <span className={styles.catCount}>{c.count}</span>
                                            <button className={`${styles.btn} ${styles.btnSm}`} style={{ height : "28px" }} onClick={() => { setEditingId(c.id); setEditingName(c.name); }}>이름 변경</button>
                                            {c.count === 0 ? <button className={`${styles.btn} ${styles.btnSm} ${styles.btnDanger}`} style={{ height : "28px" }} disabled={busy} onClick={() => remove(c)}>삭제</button> : null}
                                        </>
                                }
                            </div>
                        ))}
                    </div>
                    <div className={styles.modalFooter} style={{ flexDirection : "column", alignItems : "stretch", gap : "8px" }}>
                        <div className={styles.rowFlex}>
                            <input className={styles.input} value={newName} placeholder="새 카테고리 이름" aria-label="새 카테고리 이름" onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") add(); }} />
                            <button className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy || !newName.trim()} onClick={add}>추가</button>
                        </div>
                        <div className={styles.helper}>삭제는 항목이 0개일 때만 가능. 이름을 바꾸면 소속 항목 전체가 함께 바뀌고 미배포 상태가 됩니다.</div>
                    </div>
                </div>
            </ModalDialog>
        </Modal>
    );
};

export default FaqCategoryModal;
